import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import portalApi, { portalFileUrl } from "@/lib/portalApi";
import { StatusChip, ProgressBar } from "./PortalJobs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Check, Loader2, FileText, Download, Send, CheckCircle2,
} from "lucide-react";

const money = (n) => `$${(n ?? 0).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`;
const prettyDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
};

const INVOICE_CHIP = {
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
};

export default function PortalJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("progress");

  const load = () => portalApi.get(`/portal/jobs/${id}`).then((r) => setJob(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!job) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  const s = job.settings || {};
  const tabs = [
    s.progress !== false && ["progress", "Progress"],
    s.diary !== false && ["updates", "Updates"],
    s.photos !== false && ["photos", "Photos"],
    (s.invoices !== false || s.variations !== false) && ["money", "Money"],
    s.documents !== false && ["documents", "Documents"],
    ["messages", "Messages"],
  ].filter(Boolean);

  return (
    <div data-testid="portal-job-page">
      <Link to="/portal/jobs" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="portal-job-back">
        <ArrowLeft className="w-4 h-4" /> All jobs
      </Link>

      <div className="mt-3 rounded-2xl bg-white border border-zinc-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-mono text-zinc-400">{job.job_number}</div>
            <h1 className="text-xl font-extrabold tracking-tight text-zinc-900 leading-tight mt-0.5">{job.title}</h1>
            {job.site_address && (
              <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-2">
                <MapPin className="w-3.5 h-3.5" /> {job.site_address}
              </div>
            )}
          </div>
          <StatusChip status={job.status} />
        </div>
        {job.progress && job.progress.pct != null && (
          <div className="mt-4"><ProgressBar pct={job.progress.pct} /></div>
        )}
      </div>

      <div className="flex gap-1.5 mt-4 overflow-x-auto pb-1 -mx-1 px-1">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`px-4 h-9 rounded-lg text-[13px] font-semibold whitespace-nowrap transition-colors border ${
                    tab === k ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:text-zinc-900"
                  }`}
                  data-testid={`portal-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "progress" && <ProgressTab job={job} />}
        {tab === "updates" && <UpdatesTab job={job} />}
        {tab === "photos" && <PhotosTab job={job} />}
        {tab === "money" && <MoneyTab job={job} onChanged={load} />}
        {tab === "documents" && <DocumentsTab job={job} />}
        {tab === "messages" && <MessagesTab jobId={job.id} />}
      </div>
    </div>
  );
}

function Card({ children, testid }) {
  return <div className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid={testid}>{children}</div>;
}

function Empty({ text }) {
  return <div className="rounded-2xl bg-white border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">{text}</div>;
}

function ProgressTab({ job }) {
  const tasks = job.tasks || [];
  return (
    <div className="space-y-4">
      <Card testid="portal-progress-card">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Started</div>
            <div className="font-semibold text-zinc-900 mt-1">{prettyDate(job.start_date)}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Target finish</div>
            <div className="font-semibold text-zinc-900 mt-1">{job.end_date ? prettyDate(job.end_date) : "TBC"}</div>
          </div>
        </div>
      </Card>
      {tasks.length === 0 ? (
        <Empty text="No task breakdown shared for this job yet." />
      ) : (
        <Card testid="portal-tasks-card">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-3">Work items</div>
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3" data-testid={`portal-task-${t.id}`}>
                <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-emerald-500 text-white" : "border-2 border-zinc-300"}`}>
                  {t.status === "done" && <Check className="w-3.5 h-3.5" />}
                </span>
                <span className={`text-sm ${t.status === "done" ? "text-zinc-400 line-through" : "text-zinc-800 font-medium"}`}>{t.title}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function UpdatesTab({ job }) {
  const diary = job.diary || [];
  if (diary.length === 0) return <Empty text="No site updates posted yet — check back soon." />;
  return (
    <div className="space-y-3">
      {diary.map((d) => (
        <Card key={d.id} testid={`portal-diary-${d.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5F15]">{prettyDate(d.date)}</div>
          <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap mt-2">{d.content}</p>
        </Card>
      ))}
    </div>
  );
}

function PhotosTab({ job }) {
  const photos = job.photos || [];
  if (photos.length === 0) return <Empty text="No site photos shared yet." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portal-photos-grid">
      {photos.map((p) => (
        <a key={p.id} href={portalFileUrl(p.storage_path)} target="_blank" rel="noreferrer"
           className="block rounded-xl overflow-hidden bg-white border border-zinc-200">
          <img src={portalFileUrl(p.storage_path)} alt={p.caption || "Site photo"} className="w-full h-32 object-cover" loading="lazy" />
          <div className="p-2">
            <div className="text-[11px] text-zinc-600 truncate">{p.caption || "Site photo"}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">{prettyDate(p.taken_at)}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

function MoneyTab({ job, onChanged }) {
  const invoices = job.invoices || [];
  const variations = job.variations || [];
  const [busy, setBusy] = useState(null);

  const approve = async (vid) => {
    setBusy(vid);
    try {
      await portalApi.post(`/portal/variations/${vid}/approve`);
      toast.success("Variation approved — the team has been notified");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4">
      {job.settings?.invoices !== false && (
        <Card testid="portal-invoices-card">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-3">Invoices</div>
          {invoices.length === 0 ? (
            <div className="text-sm text-zinc-500">No invoices issued yet.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {invoices.map((inv) => (
                <div key={inv.id} className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0" data-testid={`portal-invoice-${inv.id}`}>
                  <div>
                    <div className="font-semibold text-sm text-zinc-900">{inv.invoice_number}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">Issued {prettyDate(inv.issue_date)} · Due {prettyDate(inv.due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-sm">{money(inv.total)}</div>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${INVOICE_CHIP[inv.status] || "bg-zinc-100 text-zinc-600"}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {job.settings?.variations !== false && (
        <Card testid="portal-variations-card">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-3">Variations (extra work)</div>
          {variations.length === 0 ? (
            <div className="text-sm text-zinc-500">No variations on this job.</div>
          ) : (
            <div className="space-y-3">
              {variations.map((v) => (
                <div key={v.id} className="rounded-xl border border-zinc-200 p-4" data-testid={`portal-variation-${v.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[11px] font-mono text-zinc-400">{v.variation_number}</div>
                      <p className="text-sm text-zinc-800 mt-1 leading-relaxed">{v.scope}</p>
                    </div>
                    {v.price != null && <div className="font-mono font-bold text-sm shrink-0">{money(v.price)}</div>}
                  </div>
                  <div className="mt-3">
                    {v.client_approved_at ? (
                      <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600" data-testid={`portal-variation-approved-${v.id}`}>
                        <CheckCircle2 className="w-4 h-4" /> Approved {prettyDate(v.client_approved_at)}
                      </div>
                    ) : v.price != null ? (
                      <Button size="sm" onClick={() => approve(v.id)} disabled={busy === v.id}
                              className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-semibold h-9"
                              data-testid={`portal-approve-variation-${v.id}`}>
                        {busy === v.id ? "Approving…" : "Approve this work"}
                      </Button>
                    ) : (
                      <div className="text-[12px] text-zinc-400">Being priced — you'll be able to approve it here.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function DocumentsTab({ job }) {
  const docs = job.documents || [];
  if (docs.length === 0) return <Empty text="No documents shared yet." />;
  return (
    <Card testid="portal-documents-card">
      <div className="divide-y divide-zinc-100">
        {docs.map((d) => (
          <a key={d.id || d.storage_path} href={portalFileUrl(d.storage_path)} target="_blank" rel="noreferrer"
             className="py-3 flex items-center gap-3 first:pt-0 last:pb-0 group" data-testid={`portal-doc-${d.id}`}>
            <span className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-zinc-500" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-zinc-900 truncate group-hover:text-[#FF5F15] transition-colors">{d.filename}</div>
              <div className="text-[11px] text-zinc-400">{prettyDate(d.uploaded_at)}</div>
            </div>
            <Download className="w-4 h-4 text-zinc-300 group-hover:text-[#FF5F15] transition-colors shrink-0" />
          </a>
        ))}
      </div>
    </Card>
  );
}

function MessagesTab({ jobId }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => portalApi.get(`/portal/jobs/${jobId}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await portalApi.post(`/portal/jobs/${jobId}/comments`, { text });
      setText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <Card testid="portal-messages-card">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-3">Questions & comments</div>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {comments === null && <div className="text-sm text-zinc-400">Loading…</div>}
        {comments?.length === 0 && <div className="text-sm text-zinc-500">No messages yet — ask anything about the job below.</div>}
        {comments?.map((c) => (
          <div key={c.id} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
            c.author_type === "client" ? "ml-auto bg-[#FF5F15]/10 text-zinc-900" : "bg-zinc-100 text-zinc-800"
          }`} data-testid={`portal-comment-${c.id}`}>
            <div className="text-[10px] font-bold text-zinc-500 mb-0.5">{c.author_type === "client" ? "You" : c.author_name}</div>
            {c.text}
          </div>
        ))}
      </div>
      <form onSubmit={send} className="mt-4 flex items-end gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Ask a question or leave a comment…"
                  className="rounded-lg resize-none" data-testid="portal-comment-input" />
        <Button type="submit" disabled={busy || !text.trim()}
                className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white h-10 w-10 p-0 shrink-0"
                data-testid="portal-comment-send" aria-label="Send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
}
